import React, { useEffect, useState } from "react";
import {
  ActionButtonsWrapper,
  AddAssignmentButton,
  AssignmentContentWrapper,
  AssignmentIconButton,
  AssignmentMeta,
  AssignmentTop,
  AssignmentsDescription,
  AssignmentsHeader,
  AssignmentsHeading,
  AssignmentsList,
  AssignmentsTitle,
  AssignmentsWrapper,
  AssignmentTag,
  Container,
  EmptyAssignments,
  LoadingText,
} from "./AssignmentsContainer.styles";
import { Assignment } from "../../services/assignmentService/assignmentService.types";
import { CreateModal, ConfirmModal } from "../../components/Modals";
import { FileDisplay } from "../../components/FileToDisplay/FileToDisplay.types";
import {
  createAssignment,
  fetchAssignments,
  updateAssignment,
  deleteAssignment,
  fetchAssignmentById,
} from "../../services/assignmentService/assignmentService";
import { useFileProgressPolling } from "../../hooks/useFileProgressPolling";
import { useNotify } from "context/NotificationContext";
import { logger } from "utils/logger";
import { delIcon, editIcon, fileIcon as FileGlyph, linkIcon as LinkGlyph } from "assets/icons";

const AssignmentsContainer: React.FC<{
  courseId: number;
  lessonId: number;
  isSubscriptionActive: boolean;
}> = ({ courseId, lessonId, isSubscriptionActive }) => {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [assignmentToEdit, setAssignmentToEdit] = useState<Assignment | null>(null);
  const [assignmentToDelete, setAssignmentToDelete] = useState<Assignment | null>(null);
  const [storageFiles, setStorageFiles] = useState<FileDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const toast = useNotify();

  useFileProgressPolling({
    isActive: isCreateModalOpen,
    entityId: assignmentToEdit?.id,
    filesFromStorage: storageFiles,
    updateFilesCallback: (files) => setStorageFiles(files.map((file) => ({ ...file }))),
    fetchFunction: fetchAssignmentById,
    fetchParams: [lessonId, assignmentToEdit?.id || 0],
  });

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const response = await fetchAssignments(courseId, lessonId);
      setAssignments(response.results);
    } catch (error) {
      toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка загрузки заданий", life: 3000 });
      logger.error("Ошибка загрузки заданий:", error);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  const handleCreateOrEditAssignment = async (
    formData: Record<string, string | File[] | null | FileDisplay[]>,
    isEditing: boolean,
  ) => {
    const title = typeof formData.title === "string" ? formData.title : "";
    const content = typeof formData.content === "string" ? formData.content : "";
    const video_url = typeof formData.video_url === "string" ? formData.video_url : "";
    const is_required = formData.is_required === "true";
    const uploadedFiles = Array.isArray(formData.files) ? (formData.files as File[]) : [];

    setIsModalLoading(true);
    try {
      const assignmentData = {
        lesson: lessonId,
        title,
        content,
        is_required,
        video_url,
        files: uploadedFiles,
        files_from_storage: storageFiles,
      };
      if (isEditing && assignmentToEdit) {
        await updateAssignment(courseId, lessonId, assignmentToEdit.id, assignmentData);
      } else {
        await createAssignment(courseId, lessonId, assignmentData);
      }
      await loadAssignments();
    } catch (error) {
      toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка сохранения задания", life: 3000 });
      logger.error("Ошибка при сохранении задания:", error);
    } finally {
      setIsModalLoading(false);
      setIsCreateModalOpen(false);
      setAssignmentToEdit(null);
      setStorageFiles([]);
    }
  };

  const handleDeleteAssignment = async () => {
    if (!assignmentToDelete) return;
    try {
      await deleteAssignment(courseId, lessonId, assignmentToDelete.id);
      setAssignments((previous) => previous.filter((assignment) => assignment.id !== assignmentToDelete.id));
      await loadAssignments();
    } catch (error) {
      toast.show({ severity: "error", summary: "Ошибка", detail: "Ошибка удаления задания", life: 3000 });
      logger.error("Ошибка при удалении задания:", error);
    } finally {
      setIsDeleteModalOpen(false);
      setAssignmentToDelete(null);
    }
  };

  return (
    <AssignmentsWrapper>
      <AssignmentsHeader>
        <AssignmentsHeading>
          Домашние задания{assignments.length > 0 ? ` · ${assignments.length}` : ""}
        </AssignmentsHeading>
        {isSubscriptionActive && (
          <AddAssignmentButton
            variant="ghost"
            size="sm"
            onClick={() => {
              setAssignmentToEdit(null);
              setStorageFiles([]);
              setIsCreateModalOpen(true);
              setIsModalLoading(false);
            }}
          >
            Добавить задание
          </AddAssignmentButton>
        )}
      </AssignmentsHeader>

      {loading ? (
        <LoadingText>Загрузка...</LoadingText>
      ) : assignments.length > 0 ? (
        <AssignmentsList>
          {assignments.map((assignment) => (
            <Container key={assignment.id}>
              <AssignmentTop>
                <AssignmentContentWrapper>
                  <AssignmentsTitle>{assignment.title}</AssignmentsTitle>
                  {assignment.content && <AssignmentsDescription>{assignment.content}</AssignmentsDescription>}
                </AssignmentContentWrapper>
                {isSubscriptionActive && (
                  <ActionButtonsWrapper>
                    <AssignmentIconButton
                      icon={editIcon}
                      altText="Редактировать задание"
                      onClick={() => {
                        setAssignmentToEdit(assignment);
                        setStorageFiles(assignment.files_from_storage);
                        setIsCreateModalOpen(true);
                        setIsModalLoading(false);
                      }}
                    />
                    <AssignmentIconButton
                      $danger
                      tone="danger"
                      icon={delIcon}
                      altText="Удалить задание"
                      onClick={() => {
                        setAssignmentToDelete(assignment);
                        setIsDeleteModalOpen(true);
                      }}
                    />
                  </ActionButtonsWrapper>
                )}
              </AssignmentTop>

              {(assignment.video_url || assignment.files_from_storage.length > 0) && (
                <AssignmentMeta>
                  {assignment.video_url && (
                    <AssignmentTag href={assignment.video_url} target="_blank" rel="noopener noreferrer">
                      <LinkGlyph aria-hidden="true" />
                      <span>ссылка</span>
                    </AssignmentTag>
                  )}
                  {assignment.files_from_storage.map((file) => {
                    const canOpen = file.status !== "processing" && file.status !== "failed" && Boolean(file.url);
                    return (
                      <AssignmentTag
                        key={file.id}
                        as={canOpen ? "a" : "span"}
                        {...(canOpen ? { href: file.url, target: "_blank", rel: "noopener noreferrer" } : {})}
                        title={file.name}
                      >
                        <FileGlyph aria-hidden="true" />
                        <span>{file.name}</span>
                      </AssignmentTag>
                    );
                  })}
                </AssignmentMeta>
              )}
            </Container>
          ))}
        </AssignmentsList>
      ) : (
        <EmptyAssignments>Домашних заданий пока нет. Ученики просто изучат материалы урока.</EmptyAssignments>
      )}

      {isCreateModalOpen && (
        <CreateModal
          title={assignmentToEdit ? "Редактировать задание" : "Новое задание"}
          hasDragAndDrop={true}
          files_from_storage={storageFiles}
          onRemoveFile={(fileId) => setStorageFiles((previous) => previous.filter((file) => file.id !== fileId))}
          submitButtonText={assignmentToEdit ? "Сохранить" : "Создать задание"}
          fields={[
            { name: "title", type: "text", placeholder: "Например, «Собрать прототип экрана»", required: true },
            { name: "content", type: "textarea", placeholder: "Что нужно сделать и в каком виде прислать", required: true },
            { name: "is_required", type: "switch", label: "Обязательное задание", hint: "Следующие уроки откроются только после того, как ученик выполнит это задание, а преподаватель примет и оценит его." },
          ]}
          onSubmit={(formData) => handleCreateOrEditAssignment(formData, Boolean(assignmentToEdit))}
          onCancel={() => {
            setIsCreateModalOpen(false);
            setStorageFiles([]);
            setIsModalLoading(false);
          }}
          initialValues={assignmentToEdit ? {
            title: assignmentToEdit.title,
            content: assignmentToEdit.content,
            video_url: assignmentToEdit.video_url || "",
            is_required: assignmentToEdit.is_required ? "true" : "false",
          } : {}}
          isLoading={isModalLoading}
        />
      )}

      {isDeleteModalOpen && (
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          title="Удаление задания"
          message={`Задание «${assignmentToDelete?.title}» будет удалено вместе с ответами учеников.`}
          onConfirm={handleDeleteAssignment}
          onCancel={() => setIsDeleteModalOpen(false)}
        />
      )}
    </AssignmentsWrapper>
  );
};

export default AssignmentsContainer;
